import type { MotionmasterToolpathSegment } from '../models/motionmaster-control.model';

/** Inch dimensions in a barrel-local frame: XY at its axis, Z0 at its bottom seat.
 * This is a geometry study, NOT a machine setup or a validated cutting recipe. */
export interface MotionmasterRingDraft {
  bodyDiameter: number;
  bodyHeight: number;
  neckDiameter: number;
  neckHeight: number;
  height: number;
  wall: number;
  toolDiameter: number;
  bridgeWidth: number;
  stickout: number;
}

export const MJR_25_RING_DRAFT: MotionmasterRingDraft = {
  bodyDiameter: 9, bodyHeight: 5.5, neckDiameter: 6.625,
  neckHeight: 0.75, height: 7, wall: 0.375,
  toolDiameter: 0.25, bridgeWidth: 0.25, stickout: 3,
};

// Match the existing viewer's provisional head kinematics. These are not measurements.
export const RING_DRAFT_PIVOT_TO_COLLET = 6.3;
export const RING_DRAFT_CLEARANCE = 0.25;
export const RING_DRAFT_THROUGH = 0.06;

export function ringDraftDimensions(d: MotionmasterRingDraft) {
  const outer = d.neckDiameter / 2;
  const inner = outer - d.wall;
  const bitRadius = d.toolDiameter / 2;
  // A radial cutter removes asin(bitRadius / radius) at EACH endpoint. The
  // requested bridge is the minimum remaining circumferential width, at the ID.
  const gap = d.bridgeWidth / inner + 2 * Math.asin(bitRadius / inner);
  const startAngle = Math.PI / 2 - gap / 2;
  const sweep = 2 * Math.PI - gap;
  return {
    outer, inner, bitRadius, gap, startAngle, sweep,
    endAngle: startAngle - sweep,
    tipRadius: inner - RING_DRAFT_THROUGH,
    pivotRadius: inner - RING_DRAFT_THROUGH + RING_DRAFT_PIVOT_TO_COLLET + d.stickout,
    axisZ: d.height + bitRadius,
  };
}

function valid(d: MotionmasterRingDraft): boolean {
  if (Object.values(d).some(v => !Number.isFinite(v) || v <= 0 || v > 100)) return false;
  const inner = d.neckDiameter / 2 - d.wall;
  if (d.bodyDiameter < d.neckDiameter || d.bodyHeight >= d.height - d.neckHeight ||
      inner <= d.toolDiameter || inner <= RING_DRAFT_THROUGH ||
      d.stickout <= d.wall + RING_DRAFT_THROUGH || d.stickout <= 0.3) return false;
  const { gap } = ringDraftDimensions(d);
  return Number.isFinite(gap) && gap > 0 && gap < Math.PI / 2;
}

const fields: [string, keyof MotionmasterRingDraft][] = [
  ['BODY', 'bodyDiameter'], ['BODY_HEIGHT', 'bodyHeight'],
  ['NECK', 'neckDiameter'], ['NECK_HEIGHT', 'neckHeight'], ['HEIGHT', 'height'],
  ['WALL', 'wall'], ['CUTTER', 'toolDiameter'], ['BRIDGE', 'bridgeWidth'], ['STICKOUT', 'stickout'],
];

/** Comments travel with the saved version; old programs remain unchanged. */
export function readMotionmasterRingDraft(text: string): MotionmasterRingDraft | null {
  if (!/^;@RING-DRAFT 1\s*$/m.test(text)) return null;
  const blocks = text.split(/\r?\n/).filter(line => !line.startsWith('%'))
    .map(line => line.split(';', 1)[0]).join('\n');
  // This context is local inches with the existing, uncompensated head model.
  // Hide it rather than silently ignoring units, offsets or tool compensation.
  if (!/\bG70\b/.test(blocks) || /\bG(?:71|4[0-4]|5[3-9]|92)\b|\bORG[XYZ]/.test(blocks)) return null;
  const data: Partial<MotionmasterRingDraft> = {};
  const metadata = text.split(/\r?\n/).filter(line => line.startsWith(';@RING ')).join(' ');
  for (const [word, key] of fields) {
    const match = new RegExp(`\\b${word} ([0-9]+(?:\\.[0-9]+)?)(?=\\s|$)`).exec(metadata);
    if (!match) return null;
    data[key] = Number(match[1]);
  }
  return valid(data as MotionmasterRingDraft) ? data as MotionmasterRingDraft : null;
}

/** One explicit partial circle, radial entry/exit and stop. No subroutines,
 * offsets, spindle command, feed selection, homing or machine transfer moves. */
export function buildMotionmasterRingDraft(d: MotionmasterRingDraft): string {
  if (!valid(d)) throw new Error('Invalid ring draft dimensions');
  const g = ringDraftDimensions(d);
  const f = (n: number) => (Math.abs(n) < 0.000005 ? 0 : n).toFixed(5);
  const xy = (angle: number, radius: number) => [radius * Math.cos(angle), radius * Math.sin(angle)];
  const u = (angle: number) => angle * 180 / Math.PI + 270;
  const airRadius = g.outer + RING_DRAFT_CLEARANCE + RING_DRAFT_PIVOT_TO_COLLET + d.stickout;
  const [ax, ay] = xy(g.startAngle, airRadius);
  let [px, py] = xy(g.startAngle, g.pivotRadius);
  const lines = [
    '%MJR25 RING DRAFT,MX,',
    ';GEOMETRY DRAFT ONLY - NOT SET UP FOR MACHINE EXECUTION',
    ';LOCAL INCHES: X0 Y0 BARREL AXIS, Z0 BOTTOM SEAT. NOT MACHINE ZERO.',
    ';25 LB: FIRST CUT ONLY. NO FLIP CYCLE OR GRID PLACEMENT YET.',
    ';WALL, CUTTER AND HEAD LENGTH ASSUMED. FEED / RPM NOT SELECTED.',
    ';NO HOME / SAFE APPROACH. FULL HEAD AND FIXTURE CLEARANCE NOT CHECKED.',
    ';@RING-DRAFT 1',
    `;@RING BODY ${d.bodyDiameter} BODY_HEIGHT ${d.bodyHeight} NECK ${d.neckDiameter} NECK_HEIGHT ${d.neckHeight}`,
    `;@RING HEIGHT ${d.height} WALL ${d.wall} CUTTER ${d.toolDiameter} BRIDGE ${d.bridgeWidth}`,
    `;@RING STICKOUT ${d.stickout}`,
    `;${d.stickout} IN FROM COLLET FACE - ASSUMED; PIVOT TO COLLET 6.3 IN ASSUMED`,
    `;MIN BRIDGE ${d.bridgeWidth} IN AT INNER WALL; TOOL-CENTER GAP ${f(g.gap * 180 / Math.PI)} DEG`,
    ';BRIDGE AT +Y. KERF ABOVE FINISHED HEIGHT. V=-90, U DECREASES.',
    'G70 G90 G17',
    ';FIRST POSITION IS A LOCAL REFERENCE ONLY; ARRIVAL PATH NOT DEFINED',
    `G0 X${f(ax)} Y${f(ay)} Z${f(g.axisZ)} U${f(u(g.startAngle))} V-90`,
    'G7',
    ';RADIAL ENTRY THROUGH NECK WALL - FEED NOT YET SELECTED',
    `G1 X${f(px)} Y${f(py)}`,
    'G5',
  ];
  let angle = g.startAngle;
  for (let part = 0; part < 3; part++) {
    const next = g.startAngle - g.sweep * (part + 1) / 3;
    const [x, y] = xy(next, g.pivotRadius);
    lines.push(`G2 X${f(x)} Y${f(y)} I${f(-px)} J${f(-py)} U${f(u(next))}`);
    [px, py] = [x, y];
    angle = next;
  }
  const [ex, ey] = xy(angle, airRadius);
  lines.push('G7', ';WITHDRAW RADIALLY WITHOUT CROSSING THE BRIDGE',
    `G1 X${f(ex)} Y${f(ey)}`, 'M5', ';STOP WITH BRIDGE INTACT - NEXT OPERATION NOT YET DEFINED', 'M0', 'M30');
  return lines.join('\n') + '\n';
}

/** Do not display an intact bridge if someone edits the saved motion to cut it.
 * Verify the entire expected draft path, not just its metadata or endpoints. */
export function ringDraftMatchesPath(d: MotionmasterRingDraft, segments: MotionmasterToolpathSegment[]): boolean {
  const g = ringDraftDimensions(d);
  if (segments.length !== 6 || !segments[0].from_origin ||
      segments[1].type !== 'feed' || segments[5].type !== 'feed' ||
      segments.slice(2, 5).some(s => s.type !== 'arc')) return false;
  const tolerance = 0.0003;
  const near = (a: number, b: number) => Math.abs(a - b) < tolerance;
  const air = g.outer + RING_DRAFT_CLEARANCE;
  const at = (p: number[], radius: number, angle: number) =>
    near(p[0], radius * Math.cos(angle)) && near(p[1], radius * Math.sin(angle)) && near(p[2], g.axisZ);
  if (!segments.every(s => near(s.v, -90))) return false;
  if (!at(segments[0].tip?.slice(-1)[0] ?? [], air, g.startAngle)) return false;
  for (const [index, angle, from, to] of [
    [1, g.startAngle, air, g.tipRadius], [5, g.endAngle, g.tipRadius, air],
  ]) {
    const tips = segments[index].tip ?? [];
    if (!tips.length || !at(tips[0], from, angle) || !at(tips[tips.length - 1], to, angle)) return false;
  }
  for (let arc = 0; arc < 3; arc++) {
    const tips = segments[arc + 2].tip ?? [];
    if (tips.length < 3) return false;
    for (let i = 0; i < tips.length; i++) {
      const angle = g.startAngle - g.sweep * (arc + i / (tips.length - 1)) / 3;
      if (!at(tips[i], g.tipRadius, angle)) return false;
    }
  }
  return true;
}
